import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Switch from "@mui/material/Switch";
import lockedIcon from "images/Locked_icon.png";
import unlockedIcon from "images/Unlocked_icon.png";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import { Button } from '../../../Component/CommonFormEle';
import { fetchDeviceSettings, fetchPolicyGroupSettings, updateDeviceSetting, updatePolicyGroupSetting, updatePolicySettingLock } from "js/Api/index";
function SettingsTab({ user, policy, onApplyToAllDevices, onApplyToThisDevice, settingsFrom }) {
    const [settings, setSettings] = useState([]);
    const [autoAnswer, setAutoAnswer] = useState(false);
    const [autoAnswerAudioAlert, setAutoAnswerAudioAlert] = useState(false);
    const [autoAnswerDelay, setAutoAnswerDelay] = useState(10);
    const [contactSize, setcontactSize] = useState(3);
    
    const [doNotDisturb, setDoNotDisturb] = useState(false);

    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const [outgingCalls, setOutgoingCalls] = useState(false);
    const [policySettingCategoryGroups, setPolicySettingCategoryGroups] =
        useState([]);
    const [flashScreen, setFlashScreen] = useState(false);

    const [changedSettings, setChangedSettings] = useState(new Set());
    const [initialValues, setInitialValues] = useState({
        autoAnswer: false,
        autoAnswerAudioAlert: false,
        autoAnswerDelay: 10,
        outgingCalls: false,
        flashScreen: false,
    });

    useEffect(() => {
        if (settingsFrom === "ManageDevices") {
            fetchDeviceSettingsData();
        } else if (settingsFrom === "PolicyGroups") {
            fetchPolicyGroupSettingsData();
        }

        const timer = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, []);

    const fetchDeviceSettingsData = async () => {
        try {
            const response = await fetchDeviceSettings(user.id);
            if (response.success) {
                setSettings(response.data);
                updateSettingsState(response.data);
            } else {
                console.error("Failed to fetch device settings:", response.error);
            }
        } catch (error) {
            console.error("Error fetching device settings:", error);
        }
    };

    const fetchPolicyGroupSettingsData = async () => {
        try {
            const response = await fetchPolicyGroupSettings(policy.id);
            if (response.success) {
                setPolicySettingCategoryGroups(response.data.policySettingCategoryGroups);
                setSettings(response.data.polictSettings);
                updateSettingsState(response.data.polictSettings);
                saveInitialValues(response.data.polictSettings);
            } else {
                console.error("Failed to fetch policy group settings:", response.error);
            }
        } catch (error) {
            console.error("Error fetching policy group settings:", error);
        }
    };

    const saveInitialValues = (settingsData) => {
        const values = {
            autoAnswer: false,
            autoAnswerAudioAlert: false,
            autoAnswerDelay: 10,
            outgingCalls: false,
            flashScreen: false,
            contactSize: 3
        };

        settingsData.forEach((setting) => {
            switch (setting.name) {
                case "Auto Answer":
                    values.autoAnswer = setting.value === "true";
                    break;
                case "Auto Answer audio alert":
                    values.autoAnswerAudioAlert = setting.value === "true";
                    break;
                case "Auto Answer delay":
                    values.autoAnswerDelay = setting.value === null ? 10 : parseInt(setting.value);
                    break;
                case "Allow device to make outgoing calls":
                case "Outgoing Calls":
                    values.outgingCalls = setting.value === "true";
                    break;
                case "Flash screen for incoming calls":
                    values.flashScreen = setting.value === "true";
                    break;
                case "Contact icon size":
                    values.contactSize = setting.value ?? 0;
                    break;
                default:
                    break;
            }
        });

        setInitialValues(values);
    };

    const updateSettingsState = (settingsData) => {
        settingsData.forEach((setting) => {
            switch (setting.name) {
                case "Auto Answer":
                    setAutoAnswer(setting.value === "true");
                    break;
                case "Play Sound":
                case "Auto Answer audio alert":
                    setAutoAnswerAudioAlert(setting.value === "true");
                    setInitialValues(prev => ({
                        ...prev,
                        autoAnswerAudioAlert: setting.value === "true"
                    }));
                    break;
                case "Auto Answer delay":
                    const delayValue = setting.value === null ? 10 : parseInt(setting.value);
                    setAutoAnswerDelay(delayValue);
                    setInitialValues(prev => ({
                        ...prev,
                        autoAnswerDelay: delayValue
                    }));
                    break;
                case "Allow device to make outgoing calls":
                case "Outgoing Calls":
                    setOutgoingCalls(setting.value === "true");
                    setInitialValues(prev => ({
                        ...prev,
                        outgingCalls: setting.value === "true"
                    }));
                case "Enable Do Not Disturn":
                case "Do Not Disturb":
                    setDoNotDisturb(setting.value !== "");
                    break;
                case "Flash screen for incoming calls":
                    setFlashScreen(setting.value === "true");
                    break;
                case "Contact icon size":
                    setcontactSize(setting.value);
                    break;
                default:
                    break;
            }
        });
    };

    const handleSaveSettings = async () => {
        const updatedSettings = settings.filter((setting) => {

            const settingChanged = changedSettings.has(setting.name);
            if (!settingChanged) {
                return false;
            }
            switch (setting.name) {
                case "Auto Answer":
                    setting.value = autoAnswer.toString();
                    return true;
                case "Play Sound":
                case "Auto Answer audio alert":
                    setting.value = autoAnswerAudioAlert.toString();
                    return true;
                case "Auto Answer delay":
                    setting.value = autoAnswerDelay.toString();
                    return true;
                case "Allow device to make outgoing calls":
                case "Outgoing Calls":
                    setting.value = outgingCalls.toString();
                    return true;
                case "Flash screen for incoming calls":
                    setting.value = flashScreen.toString();
                    return true;
                case "Contact icon size":
                    setting.value = contactSize.toString();
                    return true;
                default:
                    return false;
            }
        });

        try {

            if (updatedSettings.length === 0) {
                toast.info("No settings have been changed");
                return;
            }

            let hasError = false;

            // single settings page, not use now
            if (settingsFrom === "ManageDevices") {
                for (const setting of updatedSettings) {
                    const response = await updateDeviceSetting(setting);

                    if (!response.success) {
                        console.error(`Failed to update setting ${setting.name}:`, response.error);
                        hasError = true;
                    }
                }
                //policy group settings
            } else if (settingsFrom === "PolicyGroups") {
                for (const setting of updatedSettings) {
                    const response = await updatePolicyGroupSetting(policy.id, setting.id, setting.value);

                    if (!response.success) {
                        console.error(`Failed to update policy setting ${setting.name}:`, response.error);
                        hasError = true;
                    }
                }
            }

            if (hasError) {
                toast.error("Some settings failed to update");
            } else {
                toast.success("Settings updated successfully!");
                // Reset changed settings set
                setChangedSettings(new Set());
                // Refresh latest settings
                if (settingsFrom === "PolicyGroups") {
                    fetchPolicyGroupSettingsData();
                } else {
                    fetchDeviceSettingsData();
                }
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to update settings");
        }
    };

    const handleAutoAnswerChange = (event) => {
        const newValue = event.target.checked;
        setAutoAnswer(newValue);
        if (newValue !== initialValues.autoAnswer) {
            setChangedSettings(prev => new Set([...prev, "Auto Answer"]));
        } else {
            setChangedSettings(prev => {
                const newSet = new Set(prev);
                newSet.delete("Auto Answer");
                return newSet;
            });
        }
    };

    const handleAutoAnswerAudioAlertChange = (event) => {
        const newValue = event.target.checked;
        setAutoAnswerAudioAlert(newValue);

        const settingName = settings.find(
            s => s.name === "Play Sound" || s.name === "Auto Answer audio alert"
        )?.name || "Auto Answer audio alert";

        if (newValue !== initialValues.autoAnswerAudioAlert) {
            setChangedSettings(prev => new Set([...prev, settingName]));
        } else {
            setChangedSettings(prev => {
                const newSet = new Set(prev);
                newSet.delete(settingName);
                return newSet;
            });
        }
    };

    const handleAutoAnswerDelayChange = (event) => {
        const value = event.target.value;

        if (value === "") {
            return;
        }

        const newValue = parseInt(value);
        if (!isNaN(newValue) && newValue >= 1 && newValue <= 60) {
            setAutoAnswerDelay(newValue);

            if (newValue !== initialValues.autoAnswerDelay) {
                setChangedSettings(prev => new Set([...prev, "Auto Answer delay"]));
            } else {
                setChangedSettings(prev => {
                    const newSet = new Set(prev);
                    newSet.delete("Auto Answer delay");
                    return newSet;
                });
            }
        }
    };

    const handleFlashScreenChange = (event) => {
        const newValue = event.target.checked;
        setFlashScreen(newValue);
        if (newValue !== initialValues.flashScreen) {
            setChangedSettings(prev => new Set([...prev, "Flash screen for incoming calls"]));
        } else {
            setChangedSettings(prev => {
                const newSet = new Set(prev);
                newSet.delete("Flash screen for incoming calls");
                return newSet;
            });
        }
    };

    const handleOutgoingCalls = (event) => {
        const newValue = event.target.checked;
        setOutgoingCalls(newValue);

        const settingName = settings.find(
            s => s.name === "Allow device to make outgoing calls" || s.name === "Outgoing Calls"
        )?.name || "Outgoing Calls";

        if (newValue !== initialValues.outgingCalls) {
            setChangedSettings(prev => new Set([...prev, settingName]));
        } else {
            setChangedSettings(prev => {
                const newSet = new Set(prev);
                newSet.delete(settingName);
                return newSet;
            });
        }
    };

    const getCategoryLockedStatus = (categoryName) => {
        const category = policySettingCategoryGroups.find(
            (category) => category.categoryName === categoryName
        );
        return category ? category.locked : false;
    };

    const handleCategoryLockChange = async (categoryName, locked) => {
        const category = policySettingCategoryGroups.find(
            (category) => category.categoryName === categoryName
        );

        if (!category) return;

        try {
            const response = await updatePolicySettingLock(policy.id, category.id, locked);
            if (response.success) {
                // Update local state
                category.locked = locked;
                setPolicySettingCategoryGroups([...policySettingCategoryGroups]);
            } else {
                console.error("Failed to update category lock:", response.error);
                toast.error("Failed to update category lock");
            }
        } catch (error) {
            console.error("Error updating category lock:", error);
            toast.error("Failed to update category lock");
        }
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h4 className="d-inline">Call Answering</h4>
                    <div className="dropdown d-inline">
                        <button
                            className="btn lockerBtn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            {getCategoryLockedStatus("Incoming Calls") ? (
                                <img src={lockedIcon} style={{ height: "20px" }} />
                            ) : (
                                <img src={unlockedIcon} style={{ height: "20px" }} />
                            )}
                        </button>

                        <ul className="dropdown-menu dropdown-item-ul">
                            <li
                                onClick={() => handleCategoryLockChange("Incoming Calls", false)}
                            >
                                <span>
                                    <LockOpenRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0 5px",
                                        }}
                                    />
                                </span>
                                Allow device user and family to change these settings
                                {!getCategoryLockedStatus("Incoming Calls") && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                            <li
                                onClick={() => handleCategoryLockChange("Incoming Calls", true)}
                            >
                                <span>
                                    <LockRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0px 5px",
                                        }}
                                    />
                                </span>
                                Organisation manages these settings
                                {getCategoryLockedStatus("Incoming Calls") && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Auto-answer incoming calls
                </div>
                <div className="col-md-8">
                    <Switch
                        checked={autoAnswer}
                        onChange={handleAutoAnswerChange}
                        color="primary"
                        disabled={!getCategoryLockedStatus("Incoming Calls")}
                    />
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Auto-answer delay
                </div>
                <div className="col-md-8">
                    <input type="number" value={autoAnswerDelay} onChange={handleAutoAnswerDelayChange}
                        className="form-control d-inline" style={{ width: "60px", marginRight: "5px" }}
                        disabled={!getCategoryLockedStatus("Incoming Calls")}
                    />
                    s
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Play sound for incoming calls
                </div>
                <div className="col-md-8">
                    <Switch
                        checked={autoAnswerAudioAlert}
                        onChange={handleAutoAnswerAudioAlertChange}
                        color="primary"
                        disabled={!getCategoryLockedStatus("Incoming Calls")}
                    />
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Flash screen for incoming calls
                </div>
                <div className="col-md-8">
                    <Switch
                        checked={flashScreen}
                        onChange={handleFlashScreenChange}
                        color="primary"
                        disabled={!getCategoryLockedStatus("Incoming Calls")}
                    />
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-md-12">
                    <h4 className="d-inline">Outgoing Calls</h4>
                    <div className="dropdown d-inline">
                        <button
                            className="btn lockerBtn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            {getCategoryLockedStatus("Outgoing Calls") ? (
                                <img src={lockedIcon} style={{ height: "20px" }} />
                            ) : (
                                <img src={unlockedIcon} style={{ height: "20px" }} />
                            )}
                        </button>

                        <ul className="dropdown-menu dropdown-item-ul">
                            <li
                                onClick={() => handleCategoryLockChange("Outgoing Calls", false)}
                            >
                                <span>
                                    <LockOpenRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0 5px",
                                        }}
                                    />
                                </span>
                                Allow device user and family to change these settings
                                {!getCategoryLockedStatus("Outgoing Calls") && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                            <li
                                onClick={() => handleCategoryLockChange("Outgoing Calls", true)}
                            >
                                <span>
                                    <LockRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0 5px",
                                        }}
                                    />
                                </span>
                                Organisation manages these settings
                                {getCategoryLockedStatus("Outgoing Calls") && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Contact icon size
                </div>
                <div className="col-md-8">
                    <select disabled={!getCategoryLockedStatus("Outgoing Calls")} className="form-control" id="role" name="role" value={contactSize} onChange={(e) => { setcontactSize(e.target.value); setChangedSettings(prev => new Set([...prev, "Contact icon size"])); }} style={{ display: "inline", width: "auto" }} >
                        <option value="8">Small (8 icons/page)</option>
                        <option value="3">Medium (3 icons/page)</option>
                        <option value="2">Large (2 icons/page)</option>
                    </select>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Allow device to make outgoing calls
                </div>
                <div className="col-md-8">
                    <Switch
                        checked={outgingCalls}
                        onChange={handleOutgoingCalls}
                        color="primary"
                        disabled={!getCategoryLockedStatus("Outgoing Calls")}
                    />
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">
                    Contact icon size
                </div>
                <div className="col-md-8">
                    <select disabled={!getCategoryLockedStatus("Outgoing Calls")} className="form-control" id="role" name="role" value={contactSize} onChange={(e) => { setcontactSize(e.target.value); setChangedSettings(prev => new Set([...prev, "Contact icon size"])); }} style={{ display: "inline", width: "auto" }} >
                        <option value="8">Small (8 icons/page)</option>
                        <option value="3">Medium (3 icons/page)</option>
                        <option value="2">Large (2 icons/page)</option>
                    </select>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-12">
                    {settingsFrom === "ManageDevices" ? (
                        <button onClick={handleSaveSettings} className="btn cur-p btn-dark">
                            Save
                        </button>
                    ) : (
                        <>
                            <Button onClick={handleSaveSettings} variant="black">
                                Apply
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default SettingsTab;